import { useEffect, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import SettingsIcon from '@mui/icons-material/Settings';

import { clearEditMode, dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  CheckboxUI,
  InputUI,
  OrderArrowsUI,
  CopyButtonUI,
  PasteButtonUI,
} from '../Interface';
import { Box } from '@mui/material';

import useUsers from '../../context/UsersContext';
import useParam from '../../context/ParamContext';
import useConfig from '../../context/ConfigContext';
import { DependenceTypeList } from '../../config/config';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import SourceListMachineDialog from './SourceListMachineDialog';

const EditToolbar = (props) => {
  const { disabled, push, defaultField, defualtOperationMachine } = props;

  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const maxVisOrder = Math.max(...(values.sourceListOperation?.map((item) => item.visOrder) ?? []), 0);
    const newVisOrder = maxVisOrder + 1;

    defaultField.id = uuidv4();
    defaultField.isNew = true;
    defaultField.visOrder = newVisOrder;
    defaultField.sourceListOperationMachine = [defualtOperationMachine];
    await push(defaultField);

    const fieldsToValidate = [
      `sourceListOperation.${values.sourceListOperation.length ?? 0}.operationId`,
      `sourceListOperation.${values.sourceListOperation.length ?? 0}.dependence`,
      `sourceListOperation.${values.sourceListOperation.length ?? 0}.description`,
      //`sourceListOperation.${values.sourceListOperation.length ?? 0}.quantityUnitId`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  const handleCopy = () => {
    const copiedOperations = values.sourceListOperation.map((operation) => ({
      ...operation,
      id: uuidv4(),
      isNew: true,
      sourceListOperationMachine: operation.sourceListOperationMachine.map((machine) => ({
        ...machine,
        isNew: true,
        id: uuidv4(),
      })),
    }));

    localStorage.removeItem('sourceCopiedObj');
    localStorage.setItem('sourceCopiedObj', JSON.stringify(copiedOperations));

    addNotification({
      content: 'Műveletek másolva!',
      type: NotificationType.INFO,
    });
  };

  const handlePaste = async () => {
    const operations = JSON.parse(localStorage.getItem('sourceCopiedObj')) || [];

    if (operations.length === 0) {
      addNotification({
        content: 'Nincs másolt művelet!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const maxVisOrder = Math.max(...(values.sourceListOperation?.map((item) => item.visOrder) ?? []), 0);
    let newVisOrder = maxVisOrder + 1;

    for (const item of operations) {
      item.visOrder = newVisOrder;

      newVisOrder++;

      await push(item);
    }
  };

  return (
    <GridToolbarContainer>
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
        <AddButtonUi disabled={disabled} onClick={handleClick} />
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <CopyButtonUI disabled={disabled} onClick={handleCopy} />
          <PasteButtonUI disabled={disabled} onClick={handlePaste} />
        </Box>
      </Box>
    </GridToolbarContainer>
  );
};

const SourceListOperation = ({
  disabled,
  defaultField,
  autocompleteOptions,
  selectedValuesState,
  autocompleteData,
  defualtOperationMachine,
}) => {
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { params, getParam } = useParam();
  const { config, getConfig } = useConfig();
  const { values, validateField, setFieldValue, errors, setErrors } = useFormikContext();
  const { getUser, user } = useUsers();

  useEffect(() => {
    if (!Object.keys(params).length) getParam();
  }, []);

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    if (errors.sourceListOperation?.length > 0) {
      for (const [index, operationError] of errors.sourceListOperation.entries()) {
        if (
          operationError?.sourceListOperationMachine instanceof String ||
          typeof operationError?.sourceListOperationMachine === 'string'
        ) {
          addNotification({
            content: operationError?.sourceListOperationMachine,
            type: NotificationType.ERROR,
          });

          setErrors((prevErrors) => {
            {
              const updatedErrors = { ...prevErrors };
              if (updatedErrors.sourceListOperation && Array.isArray(updatedErrors.sourceListOperation)) {
                updatedErrors.sourceListOperation[index] = {
                  ...updatedErrors.sourceListOperation[index],
                  sourceListOperationMachine: undefined,
                };
              }

              return updatedErrors;
            }
          });
          break;
        }
      }
    }
  }, [errors]);

  const defaultResourceListData = {
    open: false,
    id: null,
    valueIndex: null,
  };
  const [resourceListData, setResourceListData] = useState(defaultResourceListData);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'visOrder', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
  }, []);

  const getValueIndex = (values, id) => {
    return values.sourceListOperation.findIndex((rc) => rc.id === id);
  };

  const handleOrderChange = (currentOrder, direction) => {
    const index = values.sourceListOperation.findIndex((item) => item.visOrder === currentOrder);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (swapIndex >= 0 && swapIndex < values.sourceListOperation.length) {
      const updatedList = [...values.sourceListOperation];
      [updatedList[index], updatedList[swapIndex]] = [updatedList[swapIndex], updatedList[index]];

      updatedList[index].visOrder = currentOrder;
      updatedList[swapIndex].visOrder = currentOrder + (direction === 'up' ? -1 : 1);

      setFieldValue('sourceListOperation', updatedList);
    }
  };

  const handleDelete = (index) => {
    const findedOperation = values.sourceListOperation[index];
    if (findedOperation) {
      clearEditMode(apiRef);
      const updatedList = values.sourceListOperation.map((item) => {
        if (item.visOrder > findedOperation.visOrder) {
          return { ...item, visOrder: item.visOrder - 1 };
        }
        return item;
      });
      setFieldValue('sourceListOperation', updatedList);
    }
  };

  const isGroupFilterEnabled = () => {
    const flag = config?.onlyGroupMachine;
    return flag === true || flag === 'true' || flag === 'Y' || flag === 1 || flag === '1';
  };

  const getFilteredOperationOptions = (resourceId) => {
    const defaultOptions = autocompleteOptions?.operation ?? [];
    const useGroupFilter = isGroupFilterEnabled();

    if (!useGroupFilter) {
      return defaultOptions;
    }

    const selectedResource = autocompleteData?.resource?.find((item) => item.id === resourceId);
    const selectedGroupId = selectedResource?.resourceOneGroupId;

    if (!selectedGroupId) {
      return defaultOptions;
    }

    const allowedOperationIds = new Set(
      (autocompleteData?.operation ?? [])
        .filter((operation) => operation.resourceOneGroupId === selectedGroupId)
        .map((operation) => operation.operationId)
    );

    return defaultOptions.filter((option) => allowedOperationIds.has(option.value));
  };

  const getFilteredResourceOptions = (operationId) => {
    const defaultOptions = autocompleteOptions?.resource ?? [];
    const useGroupFilter = isGroupFilterEnabled();

    if (!useGroupFilter) {
      return defaultOptions;
    }

    const selectedOperation = autocompleteData?.operation?.find((operation) => operation.operationId === operationId);
    const selectedGroupId = selectedOperation?.resourceOneGroupId;

    if (!selectedGroupId) {
      return defaultOptions;
    }

    const allowedResourceIds = new Set(
      (autocompleteData?.resource ?? [])
        .filter((resource) => resource.resourceOneGroupId === selectedGroupId)
        .map((resource) => resource.id)
    );

    return defaultOptions.filter((option) => allowedResourceIds.has(option.value));
  };

  const isCooperationFromSourcelistEnabled = params.COOPERATION_FROM_SOURCELIST === 'Y';

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'visOrder',
        type: 'string',
        headerName: '#',
        renderCell: (params) => {
          const handleUpClick = (e) => {
            clearEditMode(apiRef);
            e.stopPropagation();
            e.preventDefault();
            handleOrderChange(params.row.visOrder, 'up');
          };
          const handleDownClick = (e) => {
            clearEditMode(apiRef);
            e.stopPropagation();
            e.preventDefault();
            handleOrderChange(params.row.visOrder, 'down');
          };
          return (
            <Box display="flex" alignItems="center">
              <Box mr={1}>{params.row.visOrder}</Box>
              <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />
            </Box>
          );
        },
      },

      {
        field: 'operationId',
        headerName: 'Név',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedOperation = autocompleteOptions?.operation?.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.operationId
          );

          return findedOperation?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const resourceId = values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.resourceId;
          const operationOptions = getFilteredOperationOptions(resourceId);
          const findedOperation = autocompleteOptions?.operation?.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.operationId
          );
          return (
            <Field
              name={`sourceListOperation.${valueIndex}.operationId`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.sourceListOperation?.[valueIndex]?.operationId} />}
              onChange={(_e, newVal) => {
                setFieldValue(`sourceListOperation.${valueIndex}.operationId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
                const selectedOperation = autocompleteData.operation.find((i) => i.operationId === newVal?.value);
                if (selectedOperation) {
                  setFieldValue(
                    `sourceListOperation.${valueIndex}.sourceListOperationMachine.0.resourceId`,
                    selectedOperation?.resourceOneId ?? null
                  );
                  data.api.setEditCellValue({
                    id: data.id,
                    field: 'firstResource',
                    value: selectedOperation?.resourceOneId ?? null,
                  });
                }
              }}
              onBlur={async () => await validateField(`sourceListOperation.${valueIndex}.operationId`)}
              value={findedOperation}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={operationOptions}
            />
          );
        },
      },
      {
        field: 'dependence',
        headerName: 'Függőség',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedDependence = DependenceTypeList.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.dependence
          );

          return findedDependence?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedDependence = DependenceTypeList.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.dependence
          );
          return (
            <Field
              name={`sourceListOperation.${valueIndex}.dependence`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.sourceListOperation?.[valueIndex]?.dependence} />}
              onChange={(_e, newVal) => {
                setFieldValue(`sourceListOperation.${valueIndex}.dependence`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () => await validateField(`sourceListOperation.${valueIndex}.dependence`)}
              value={findedDependence}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={DependenceTypeList}
            />
          );
        },
      },
      {
        field: 'firstResource',
        headerName: 'Erőforrás',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedOperation = autocompleteOptions.resource.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.resourceId
          );

          return findedOperation?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const operationId = values.sourceListOperation?.[valueIndex]?.operationId;
          const resourceOptions = getFilteredResourceOptions(operationId);
          const findedOperation = autocompleteOptions.resource.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.resourceId
          );
          return (
            <Field
              type="autocomplete"
              name={`sourceListOperation.${valueIndex}.sourceListOperationMachine.0.resourceId`}
              component={AutoCompleteSelectUI}
              fullWidth
              variant="standard"
              helperText={
                <FormErrorUI
                  message={errors?.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.resourceId}
                />
              }
              InputLabelProps={{ shrink: values.resourceId !== '' }}
              onChange={async (_e, newVal) => {
                await setFieldValue(
                  `sourceListOperation.${valueIndex}.sourceListOperationMachine.0.resourceId`,
                  newVal?.value ?? null
                );

                await validateField(`sourceListOperation.${valueIndex}.sourceListOperationMachine.0.resourceId`);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () =>
                await validateField(`sourceListOperation.${valueIndex}.sourceListOperationMachine.0.resourceId`)
              }
              dataset={resourceOptions}
              selectedLabelValue={findedOperation?.label}
              selectedValue={values.sourceListOperation?.[valueIndex].sourceListOperationMachine?.[0]?.resourceId}
              //disabled={!data.row.isNew}
            />
          );
        },
      },
      {
        field: 'firstResourceNeededQuantity',
        headerName: 'Ráfordítás',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.neededQuantity;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              type="number"
              as={InputUI}
              variant="standard"
              name={`sourceListOperation.${valueIndex}.sourceListOperationMachine.0.neededQuantity`}
              id="firstResourceNeededQuantity"
              autoFocus={focusedFieldRef.current === 'firstResourceNeededQuantity'}
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.target.select()}
              onChange={(e) => {
                setFieldValue(
                  `sourceListOperation.${valueIndex}.sourceListOperationMachine.0.neededQuantity`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              helperText={
                <FormErrorUI
                  message={errors?.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.neededQuantity}
                />
              }
              //disabled={!data.row.isNew}
            />
          );
        },
      },
      {
        field: 'quantityUnitId',
        headerName: 'Mennyiségi egység',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedOperation = autocompleteOptions.quantityUnit.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.quantityUnitId
          );

          return findedOperation?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedOperation = autocompleteOptions.quantityUnit.find(
            (u) => u.value === values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.quantityUnitId
          );
          return (
            <Field
              type="autocomplete"
              name={`sourceListOperation.${valueIndex}.sourceListOperationMachine.0.quantityUnitId`}
              //name={`sourceListOperation.${valueIndex}.quantityUnitId`}
              component={AutoCompleteSelectUI}
              fullWidth
              variant="standard"
              helperText={
                <FormErrorUI
                  message={errors?.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.quantityUnitId}
                />
              }
              InputLabelProps={{ shrink: values.resourceId !== '' }}
              onChange={async (_e, newVal) => {
                await setFieldValue(
                  `sourceListOperation.${valueIndex}.sourceListOperationMachine.0.quantityUnitId`,
                  newVal?.value ?? null
                );

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              onBlur={async () =>
                await validateField(`sourceListOperation.${valueIndex}.sourceListOperationMachine.0.quantityUnitId`)
              }
              dataset={autocompleteOptions.quantityUnit}
              selectedLabelValue={findedOperation?.label}
              selectedValue={values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[0]?.quantityUnitId}
              disabled
            />
          );
        },
      },
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              as={InputUI}
              variant="standard"
              name={`sourceListOperation.${valueIndex}.description`}
              onChange={(e) => {
                setFieldValue(`sourceListOperation.${valueIndex}.description`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              helperText={<FormErrorUI message={errors?.sourceListOperation?.[valueIndex]?.description} />}
            />
          );
        },
      },
      ...(isCooperationFromSourcelistEnabled
        ? [
            {
              field: 'cooperationCompanyId',
              headerName: 'Partner',
              type: 'string',
              editable: true,
              sortable: false,
              renderCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);
                const selectedCompanyId = values.sourceListOperation?.[valueIndex]?.cooperationCompanyId;
                return autocompleteOptions?.company?.find((company) => company.value === selectedCompanyId)?.label;
              },
              renderEditCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);
                const selectedCompanyId = values.sourceListOperation?.[valueIndex]?.cooperationCompanyId;
                const selectedCompanyLabel =
                  autocompleteOptions?.company?.find((company) => company.value === selectedCompanyId)?.label ?? '';

                return (
                  <Field
                    type="autocomplete"
                    name={`sourceListOperation.${valueIndex}.cooperationCompanyId`}
                    component={AutoCompleteSelectUI}
                    fullWidth
                    variant="standard"
                    helperText={
                      <FormErrorUI message={errors?.sourceListOperation?.[valueIndex]?.cooperationCompanyId} />
                    }
                    onChange={async (_e, newVal) => {
                      await setFieldValue(
                        `sourceListOperation.${valueIndex}.cooperationCompanyId`,
                        newVal?.value ?? null
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: newVal?.value ?? null,
                      });
                    }}
                    onBlur={async () =>
                      await validateField(`sourceListOperation.${valueIndex}.cooperationCompanyId`)
                    }
                    dataset={autocompleteOptions?.company ?? []}
                    selectedValue={selectedCompanyId}
                    selectedLabelValue={selectedCompanyLabel}
                  />
                );
              },
            },
          ]
        : []),
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const valueIndex = getValueIndex(values, id);
                handleDelete(valueIndex);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      ...(params.HIDE_IN_PRODUCTION_CHECK_IN_POINT_FIELD !== 'Y'
        ? [
            {
              field: 'chk',
              type: 'actions',
              cellClassName: 'actions',
              headerName: 'Lejelentési pont',
              sortable: false,
              getActions: ({ id, row }) => {
                return [<Field name="a" type="checkbox" as={CheckboxUI} fullWidth disabled={disabled} />];
              },
            },
          ]
        : []),
      {
        field: 'fixedLeadTime',
        headerName: 'Fix átfutási idő',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              type="number"
              as={InputUI}
              variant="standard"
              name={`sourceListOperation.${valueIndex}.fixedLeadTime`}
              id="fixedLeadTime"
              autoFocus={focusedFieldRef.current === 'fixedLeadTime'}
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.target.select()}
              onChange={(e) => {
                setFieldValue(`sourceListOperation.${valueIndex}.fixedLeadTime`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              helperText={<FormErrorUI message={errors?.sourceListOperation?.[valueIndex]?.fixedLeadTime} />}
              //disabled={!data.row.isNew}
            />
          );
        },
      },
      ...(params.SOURCELIST_ENABLE_MULTIRES === 'Y'
        ? [
            {
              field: 'multiRes',
              type: 'actions',
              cellClassName: 'actions',
              headerName: 'Erőforrások',
              sortable: false,
              getActions: ({ id, row }) => {
                const valueIndex = getValueIndex(values, id);
                return [
                  <GridActionsCellItem
                    icon={<SettingsIcon />}
                    label="Resources"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setResourceListData({ open: true, id, valueIndex });
                    }}
                    color="inherit"
                    disabled={disabled}
                  />,
                ];
              },
            },
          ]
        : []),
    ]);
  };

  const handleArrowKeyNavigation = (params, event) => {
    const key = event.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return;

    const rowId = params.id;
    const mode = apiRef.current.getRowMode(rowId);
    if (mode !== 'edit') return;

    apiRef.current.stopRowEditMode({ id: rowId });

    const rowIndex = values.sourceListOperation.findIndex((row) => row.id === rowId);
    const offset = key === 'ArrowDown' ? 1 : -1;
    const nextRow = values.sourceListOperation[rowIndex + offset];
    if (!nextRow) return;

    apiRef.current.setCellFocus(nextRow.id, params.field);
    apiRef.current.startRowEditMode({ id: nextRow.id });
    focusedFieldRef.current = params.field;

    event.preventDefault();
    event.stopPropagation();
  };

  const selectedDialogOperationId =
    resourceListData.valueIndex !== null
      ? values?.sourceListOperation?.[resourceListData.valueIndex]?.operationId
      : null;
  const filteredDialogResourceOptions =
    resourceListData.valueIndex !== null ? getFilteredResourceOptions(selectedDialogOperationId) : [];

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="sourceListOperation">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.sourceListOperation}
                  height={480}
                  datagridInitialState={initialState}
                  columns={columns(setFieldValue, validateField, errors, remove)}
                  rows={values?.sourceListOperation ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultField,
                      defualtOperationMachine,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;

                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    focusedFieldRef.current = params.field;
                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                  onCellKeyDown={handleArrowKeyNavigation}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>

      <SourceListMachineDialog
        onClose={() => {
          setResourceListData(defaultResourceListData);
        }}
        autocompleteOptions={autocompleteOptions}
        resourceOptions={filteredDialogResourceOptions}
        selectedValuesState={selectedValuesState}
        defualtOperationMachine={defualtOperationMachine}
        {...resourceListData}
      />
    </>
  );
};

export default SourceListOperation;
